import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { fetchUsersCount } from "@/lib/userApi";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Trophy } from "lucide-react";
import { useTabNavigation } from "@/stores/use-nav-store";
import { cn } from "@/lib/utils";

export function IntroductoryCard() {
  const { t } = useTranslation();
  const { isLargeScreen } = useTabNavigation(768);
  const { data: membersCount } = useQuery({
    queryKey: ["total-users"],
    queryFn: fetchUsersCount,
  });

  return (
    <div
      className={cn(
        " shadow border md:flex flex-col overflow-hidden mx-auto bg-background",
        {
          "rounded-xl": isLargeScreen,
        }
      )}
    >
      <div className="px-4 pt-4 flex flex-col flex-1">
        <h2 className="font-semibold text-lg sm:text-xl mb-1 text-foreground">
          {t("community.introductoryCard.welcomeTitle")}
        </h2>

        <div className="block sm:hidden">
          <Accordion type="single" collapsible defaultValue="">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-sm font-medium px-0 py-2">
                {t("community.introductoryCard.more")}
              </AccordionTrigger>
              <AccordionContent>
                <IntroText />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="hidden sm:block">
          <IntroText />
        </div>
      </div>

      <div className="border-t px-4 py-3 flex items-center">
        <div>
          <span className="font-bold text-xl sm:text-2xl">
            {membersCount ?? "…"}
          </span>
          <span className="text-xs sm:text-sm ml-1 text-gray-500">
            {t("community.introductoryCard.members")}
          </span>
        </div>
      </div>

      <div className="px-4 pb-4 md:hidden">
        <Link to="/leaderboard">
          <Button
            className="w-full rounded-xl text-base py-2 font-semibold flex items-center justify-center gap-1"
            variant="secondary"
          >
            <Trophy className="w-5 h-5 text-blue-400" />
            {t("community.introductoryCard.leaderboard")}
          </Button>
        </Link>
      </div>
    </div>
  );
}

function IntroText() {
  const { t } = useTranslation();
  return (
    <div className="text-sm sm:text-base text-foreground mb-3">
      <span
        dangerouslySetInnerHTML={{
          __html: t("community.introductoryCard.introText"),
        }}
      />
      <ul className="list-disc list-inside mt-1">
        <li>{t("community.introductoryCard.list.share")}</li>
        <li>{t("community.introductoryCard.list.support")}</li>
        <li>{t("community.introductoryCard.list.events")}</li>
        <li>{t("community.introductoryCard.list.grow")}</li>
      </ul>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="font-medium text-foreground mb-2">
          {t("community.introductoryCard.rulesSection.rulesTitle")}
        </p>

        <div className="mb-3">
          <p className="font-medium text-red-600 mb-1">
            {t("community.introductoryCard.rulesSection.prohibited.title")}
          </p>
          <ul className="text-foreground space-y-1">
            {t("community.introductoryCard.rulesSection.prohibited.items", {
              returnObjects: true,
            }).map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-foreground">
          {t("community.introductoryCard.rulesSection.conclusion")}
        </p>
      </div>
    </div>
  );
}
